import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const CONSENT_KEY = "freitag_cookie_consent";

const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const setConsent = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({
    ...consent,
    domain: window.location.hostname,
    timestamp: new Date().toISOString(),
  }));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [categories, setCategories] = useState({
    necessary: true,
    statistics: false,
    marketing: false,
  });

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { necessary: true, statistics: true, marketing: true };
    setConsent(consent);
    setVisible(false);
    window.dispatchEvent(new CustomEvent("consentUpdated", { detail: consent }));
  };

  const handleAcceptSelected = () => {
    const consent = { ...categories, necessary: true };
    setConsent(consent);
    setVisible(false);
    window.dispatchEvent(new CustomEvent("consentUpdated", { detail: consent }));
  };

  const handleRejectAll = () => {
    const consent = { necessary: true, statistics: false, marketing: false };
    setConsent(consent);
    setVisible(false);
    window.dispatchEvent(new CustomEvent("consentUpdated", { detail: consent }));
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="cookie-consent">
      <div className="w-full max-w-2xl bg-[hsl(220,18%,10%)] border border-[hsl(220,14%,20%)] text-white p-6 md:p-8 shadow-lg">
        <h3 className="font-heading font-bold text-lg text-white mb-3">
          Cookie-Einstellungen
        </h3>
        <p className="text-sm text-gray-300 font-body leading-relaxed mb-4">
          {"Wir verwenden Cookies und \u00e4hnliche Technologien. Technisch notwendige Cookies sind f\u00fcr den Betrieb der Website erforderlich. Weitere Cookies werden nur mit Ihrer ausdr\u00fccklichen Einwilligung gesetzt."}
        </p>
        <p className="text-xs text-gray-400 font-body mb-6">
          {"Weitere Informationen finden Sie in unserer "}
          <Link to="/datenschutz" className="underline text-brand-blue-light hover:text-white">
            {"Datenschutzerkl\u00e4rung"}
          </Link>.
        </p>

        {showDetails && (
          <div className="space-y-3 mb-6 border border-[hsl(220,14%,20%)] p-4 bg-[hsl(220,18%,7%)]">
            <label className="flex items-center gap-3 cursor-not-allowed opacity-70">
              <input type="checkbox" checked disabled className="accent-brand-blue" />
              <div>
                <span className="text-sm text-white font-heading font-semibold">Notwendig</span>
                <p className="text-xs text-gray-400 font-body">{"Erforderlich f\u00fcr den Betrieb der Website. Kann nicht deaktiviert werden."}</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={categories.statistics}
                onChange={(e) => setCategories(prev => ({ ...prev, statistics: e.target.checked }))}
                className="accent-brand-blue"
              />
              <div>
                <span className="text-sm text-white font-heading font-semibold">Statistik</span>
                <p className="text-xs text-gray-400 font-body">Anonyme Nutzungsstatistiken zur Verbesserung der Website.</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={categories.marketing}
                onChange={(e) => setCategories(prev => ({ ...prev, marketing: e.target.checked }))}
                className="accent-brand-blue"
              />
              <div>
                <span className="text-sm text-white font-heading font-semibold">Marketing</span>
                <p className="text-xs text-gray-400 font-body">{"Cookies f\u00fcr personalisierte Werbung und Remarketing."}</p>
              </div>
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button variant="blue" size="default" onClick={handleAcceptAll} data-testid="cookie-accept-all">
            Alle akzeptieren
          </Button>
          {showDetails ? (
            <Button variant="outline" size="default" onClick={handleAcceptSelected} data-testid="cookie-accept-selected">
              Auswahl speichern
            </Button>
          ) : (
            <Button variant="outline" size="default" onClick={() => setShowDetails(true)} data-testid="cookie-settings">
              Einstellungen
            </Button>
          )}
          <Button variant="outline" size="default" onClick={handleRejectAll} data-testid="cookie-reject-all">
            Nur notwendige
          </Button>
        </div>
      </div>
    </div>
  );
};

export const reopenConsentBanner = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
};
